import { useLang } from "@/lib/i18n";
import { Seo } from "@/lib/seo";
import { useSiteConfig } from "@/context/SiteConfigContext";
import { SectionHeading, ComplianceNote } from "@/components/shared/Shared";

function Block({ title, children }) {
  return (
    <div className="mt-9">
      <h2 className="font-display text-xl font-semibold text-[#111318]">{title}</h2>
      <div className="mt-3 text-sm text-[#3B4452] leading-relaxed space-y-3">{children}</div>
    </div>
  );
}

export default function Legal({ page }) {
  const { t } = useLang();
  const site = useSiteConfig();
  const isImpressum = page === "impressum";

  return (
    <div className="bg-white">
      <Seo
        title={isImpressum ? t("Impressum", "Legal Notice") : t("Datenschutzerkl\u00e4rung", "Privacy Policy")}
        description={t(
          "Rechtliche Informationen der Eleven Family Office GmbH, K\u00f6nigstra\u00dfe 27, 70173 Stuttgart.",
          "Legal information of Eleven Family Office GmbH, K\u00f6nigstra\u00dfe 27, 70173 Stuttgart."
        )}
      />
      <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 py-12 sm:py-16" data-testid={`legal-${page}`}>
        <SectionHeading
          eyebrow={t("Rechtliches", "Legal")}
          title={isImpressum ? t("Impressum", "Legal Notice") : t("Datenschutzerkl\u00e4rung", "Privacy Policy")}
        />

        {isImpressum ? (
          <div>
            <Block title={t("Angaben gem\u00e4\u00df \u00a7 5 TMG", "Information according to \u00a7 5 TMG")}>
              <p data-testid="impressum-address">{site.legal_name}<br />{site.street}<br />{site.zip_city}<br />{site.country}</p>
              <p>{t(`Handelsregister: ${site.register_info} (Platzhalter)`, `Commercial register: ${site.register_info} (placeholder)`)}</p>
              <p>{t("Vertreten durch die Gesch\u00e4ftsf\u00fchrung (Platzhalter)", "Represented by the management (placeholder)")}</p>
            </Block>
            <Block title={t("Kontakt", "Contact")}>
              <p data-testid="impressum-contact">{t("Telefon", "Phone")}: {site.phone}<br />E-Mail: {site.email}</p>
            </Block>
            <Block title={t("Aufsicht & Erlaubnisse", "Supervision & Licenses")}>
              <p>{t(
                "Angaben zu Erlaubnissen nach \u00a7 34d GewO (Versicherungsvermittlung) und zust\u00e4ndiger Aufsichtsbeh\u00f6rde werden hier erg\u00e4nzt. (Platzhalter \u2013 bitte mit tats\u00e4chlichen Registrierungsdaten bef\u00fcllen.)",
                "Information on licenses according to \u00a7 34d GewO (insurance mediation) and the competent supervisory authority will be added here. (Placeholder \u2013 please fill with actual registration data.)"
              )}</p>
            </Block>
            <Block title={t("Haftungshinweis", "Liability Notice")}>
              <p>{t(
                "Alle Inhalte dienen der allgemeinen Information und stellen keine Anlage-, Steuer- oder Rechtsberatung dar.",
                "All content is for general information and does not constitute investment, tax or legal advice."
              )}</p>
            </Block>
          </div>
        ) : (
          <div>
            <Block title={t("1. Verantwortlicher", "1. Controller")}>
              <p>{t(
                "Verantwortlich f\u00fcr die Datenverarbeitung auf dieser Website ist die Eleven Family Office GmbH (Kontaktdaten siehe Impressum).",
                "The controller for data processing on this website is Eleven Family Office GmbH (contact details see legal notice)."
              )}</p>
            </Block>
            <Block title={t("2. Verarbeitung bei Anfragen", "2. Processing of Inquiries")}>
              <p>{t(
                "Bei Nutzung unserer Formulare verarbeiten wir die von Ihnen angegebenen Daten (Name, Kontaktdaten, Anliegen, Kampagnenzuordnung) zur Bearbeitung Ihrer Anfrage. Rechtsgrundlage ist Art. 6 Abs. 1 lit. b DSGVO sowie Ihre Einwilligung nach Art. 6 Abs. 1 lit. a DSGVO. Einwilligungen werden dokumentiert und k\u00f6nnen jederzeit widerrufen werden.",
                "When using our forms, we process the data you provide (name, contact details, request, campaign attribution) to handle your inquiry. Legal basis is Art. 6 (1) (b) GDPR and your consent under Art. 6 (1) (a) GDPR. Consents are documented and can be revoked at any time."
              )}</p>
            </Block>
            <Block title={t("3. CRM-Systeme", "3. CRM Systems")}>
              <p>{t(
                "Zur strukturierten Bearbeitung von Anfragen setzen wir CRM-Systeme ein. Eine \u00dcbermittlung erfolgt nur auf Grundlage entsprechender Auftragsverarbeitungsvertr\u00e4ge.",
                "For structured processing of inquiries we use CRM systems. Data is transferred only on the basis of corresponding data processing agreements."
              )}</p>
            </Block>
            <Block title={t("4. Cookies & Einwilligung", "4. Cookies & Consent")}>
              <p>{t(
                "Technisch notwendige Cookies werden auf Grundlage berechtigten Interesses gesetzt. Optionale Analyse- und Marketing-Cookies nutzen wir nur mit Ihrer Einwilligung \u00fcber unseren Consent-Banner. Ihre Auswahl k\u00f6nnen Sie jederzeit \u00e4ndern.",
                "Technically necessary cookies are set on the basis of legitimate interest. We use optional analytics and marketing cookies only with your consent via our consent banner. You can change your selection at any time."
              )}</p>
            </Block>
            <Block title={t("5. Terminbuchung (Calendly)", "5. Appointment Booking (Calendly)")}>
              <p>{t(
                "F\u00fcr die Terminbuchung binden wir Calendly ein. Beim Aufruf der Terminseite werden Daten an Calendly \u00fcbertragen. Details entnehmen Sie der Datenschutzerkl\u00e4rung von Calendly.",
                "We embed Calendly for appointment booking. When accessing the booking page, data is transferred to Calendly. For details, see Calendly's privacy policy."
              )}</p>
            </Block>
            <Block title={t("6. Ihre Rechte", "6. Your Rights")}>
              <p>{t(
                "Sie haben das Recht auf Auskunft, Berichtigung, L\u00f6schung, Einschr\u00e4nkung der Verarbeitung, Daten\u00fcbertragbarkeit sowie Widerspruch. Wenden Sie sich hierzu an die im Impressum genannten Kontaktdaten.",
                "You have the right to access, rectification, erasure, restriction of processing, data portability and objection. Please use the contact details in the legal notice."
              )}</p>
            </Block>
          </div>
        )}

        <div className="mt-10">
          <ComplianceNote>
            {t(
              "Platzhalter-Struktur: Bitte vor Ver\u00f6ffentlichung durch einen Datenschutzbeauftragten / Rechtsberater pr\u00fcfen und mit den tats\u00e4chlichen Unternehmensdaten bef\u00fcllen.",
              "Placeholder structure: Please have it reviewed by a data protection officer / legal advisor and filled with actual company data before publication."
            )}
          </ComplianceNote>
        </div>
      </div>
    </div>
  );
}
